/**
 * Daemon client — HTTP client for the session daemon.
 *
 * Handles pidfile read/write, stale PID detection, auto-start,
 * and provides typed HTTP helpers for daemon communication.
 *
 * @module
 */

import { spawn } from 'node:child_process';
import { randomUUID } from 'node:crypto';
import fs from 'node:fs/promises';
import { existsSync } from 'node:fs';
import path from 'node:path';
import os from 'node:os';
import { fileURLToPath } from 'node:url';
import type {
  DaemonState,
  DaemonResponse,
  SessionInfo,
  CreateSessionRequest,
  SwapContextRequest,
  ToolResponse,
  StorageStateResponse,
} from './types.js';
import { attachCliDiagnosticMetadata, type CliDiagnosticMetadata } from '../cli-diagnostics.js';

const PIDFILE_DIR = path.join(os.homedir(), '.config', 'canary-cli');
const PIDFILE_PATH = path.join(PIDFILE_DIR, 'daemon.json');
const LOCKFILE_PATH = path.join(PIDFILE_DIR, 'daemon.spawn.lock');
const HEALTH_POLL_INTERVAL_MS = 100;
const HEALTH_POLL_TIMEOUT_MS = 15_000;
const SPAWN_LOCK_TIMEOUT_MS = 20_000;
const SPAWN_LOCK_STALE_MS = 30_000;
const DAEMON_OUTPUT_TAIL_CHARS = 2_000;

/* ── Pidfile helpers ─────────────────────────────────────────────────── */

async function readPidfile(): Promise<DaemonState | null> {
  try {
    const content = await fs.readFile(PIDFILE_PATH, 'utf-8');
    return JSON.parse(content) as DaemonState;
  } catch {
    return null;
  }
}

function isProcessAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

const DAEMON_REQUEST_TIMEOUT_MS = 30_000;
const DAEMON_FETCH_MAX_RETRIES = 2;

type DaemonRequestTelemetry = {
  operation?: string;
  requestId?: string;
  sessionId?: string;
  sessionTargetSource?: string;
};
type DaemonAttemptTelemetry = Omit<DaemonRequestTelemetry, 'requestId'> & {
  requestId: string;
  attempt: number;
};
type DaemonSpawnTelemetryParams = {
  phase: 'spawn-timeout' | 'spawn-error' | 'spawn-exit';
  cliEntry: string;
  candidates: string[];
  childPid?: number;
  startedAt: number;
  output: string;
  exitCode?: number | null;
  exitSignal?: NodeJS.Signals | null;
};

/* ── HTTP helpers ────────────────────────────────────────────────────── */

function isConnectionError(err: unknown): boolean {
  if (err instanceof TypeError && err.message === 'fetch failed') return true;
  const cause = (err as { cause?: { code?: string } })?.cause;
  if (cause?.code === 'UND_ERR_SOCKET') return true;
  if (cause?.code === 'ECONNREFUSED') return true;
  if (cause?.code === 'ECONNRESET') return true;
  return false;
}

async function daemonFetchOnce(
  port: number,
  method: string,
  path: string,
  body: unknown,
  telemetry: DaemonAttemptTelemetry
): Promise<unknown> {
  const url = `http://127.0.0.1:${port}${path}`;
  const startedAt = Date.now();
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : undefined),
        Connection: 'close',
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch (err) {
    throw attachCliDiagnosticMetadata(err, {
      ...daemonTelemetry(method, path, port, telemetry),
      cliDaemonPhase: 'request',
      cliDaemonElapsedMs: Date.now() - startedAt,
      cliDaemonTimeoutMs: DAEMON_REQUEST_TIMEOUT_MS,
    });
  }

  try {
    return await res.json();
  } catch (err) {
    throw attachCliDiagnosticMetadata(err, {
      ...daemonTelemetry(method, path, port, telemetry),
      cliDaemonPhase: 'response-parse',
      cliDaemonElapsedMs: Date.now() - startedAt,
      cliDaemonStatus: res.status,
    });
  }
}

/**
 * Fetch from daemon with retry. On connection errors (daemon died or
 * idle-exited between ensureDaemon and the request), re-ensure the
 * daemon and retry up to DAEMON_FETCH_MAX_RETRIES times.
 */
async function daemonFetchWithRetry(
  method: string,
  path: string,
  body?: unknown,
  telemetry: DaemonRequestTelemetry = {}
): Promise<unknown> {
  const requestId = telemetry.requestId ?? randomUUID();
  let port: number;
  try {
    port = await ensureDaemon();
  } catch (err) {
    throw attachCliDiagnosticMetadata(err, {
      ...daemonTelemetry(method, path, null, { ...telemetry, requestId, attempt: 0 }),
      cliDaemonPhase: 'ensure',
      cliDaemonTimeoutMs: HEALTH_POLL_TIMEOUT_MS,
    });
  }
  let lastError: unknown;

  for (let attempt = 0; attempt <= DAEMON_FETCH_MAX_RETRIES; attempt++) {
    try {
      return await daemonFetchOnce(port, method, path, body, {
        ...telemetry,
        requestId,
        attempt,
      });
    } catch (err) {
      lastError = err;
      if (!isConnectionError(err) || attempt === DAEMON_FETCH_MAX_RETRIES) {
        throw err;
      }
      // Daemon likely died — clean up stale pidfile and re-ensure
      try {
        await fs.unlink(PIDFILE_PATH);
      } catch {
        // ignore
      }
      try {
        port = await ensureDaemon();
      } catch (ensureErr) {
        throw attachCliDiagnosticMetadata(ensureErr, {
          ...daemonTelemetry(method, path, null, { ...telemetry, requestId, attempt }),
          cliDaemonPhase: 'retry-ensure',
          cliDaemonPreviousAttempt: attempt,
        });
      }
    }
  }

  throw lastError;
}

function daemonTelemetry(
  method: string,
  path: string,
  port: number | null,
  telemetry: DaemonAttemptTelemetry
): CliDiagnosticMetadata {
  return {
    cliDaemonOperation: telemetry.operation ?? `${method} ${path}`,
    cliDaemonRequestId: telemetry.requestId,
    cliDaemonMethod: method,
    cliDaemonPath: path,
    cliDaemonAttempt: telemetry.attempt,
    cliDaemonMaxRetries: DAEMON_FETCH_MAX_RETRIES,
    cliDaemonPort: port,
    cliSessionId: telemetry.sessionId ?? null,
    cliSessionTargetSource: telemetry.sessionTargetSource ?? null,
  };
}

function daemonOutputTail(output: string): string | null {
  const tail = output.trim().slice(-DAEMON_OUTPUT_TAIL_CHARS);
  return tail.length > 0 ? tail : null;
}

function daemonSpawnTelemetry(params: DaemonSpawnTelemetryParams): CliDiagnosticMetadata {
  return {
    cliDaemonOperation: 'session.daemon.spawn',
    cliDaemonPhase: params.phase,
    cliDaemonElapsedMs: Date.now() - params.startedAt,
    cliDaemonTimeoutMs: HEALTH_POLL_TIMEOUT_MS,
    cliDaemonEntrypoint: params.cliEntry,
    cliDaemonEntrypointCandidates: params.candidates.join(','),
    cliDaemonExecPath: process.execPath,
    cliDaemonPlatform: `${process.platform}/${process.arch}`,
    cliDaemonNodeVersion: process.version,
    cliDaemonSpawnPid: params.childPid ?? null,
    cliDaemonStdoutTail: daemonOutputTail(params.output),
    cliDaemonStdoutBytes: Buffer.byteLength(params.output, 'utf8'),
    cliDaemonExitCode: params.exitCode ?? null,
    cliDaemonExitSignal: params.exitSignal ?? null,
  };
}

async function healthCheck(port: number): Promise<boolean> {
  try {
    const res = await fetch(`http://127.0.0.1:${port}/health`, {
      signal: AbortSignal.timeout(2000),
      headers: { Connection: 'close' },
    });
    return res.ok;
  } catch {
    return false;
  }
}

/* ── Auto-start ──────────────────────────────────────────────────────── */

async function spawnDaemon(): Promise<number> {
  // Resolve the canary CLI entry point
  // In source: this file is src/session/daemon-client.ts → ../index.ts
  // In dist:   this file is dist/chunk-*.js → ./index.js (flat)
  const dir = path.dirname(fileURLToPath(import.meta.url));
  const candidates = [
    path.resolve(dir, '..', 'index.ts'), // source layout
    path.resolve(dir, 'index.js'), // dist layout (flat)
    path.resolve(dir, '..', 'index.js'), // fallback
  ];
  const cliEntry = candidates.find((p) => existsSync(p));
  if (!cliEntry) {
    throw new Error(`Cannot find CLI entry point. Searched: ${candidates.join(', ')}`);
  }

  const child = spawn(process.execPath, [cliEntry, 'session', 'daemon'], {
    detached: true,
    stdio: ['ignore', 'pipe', 'ignore'],
    env: { ...process.env },
    windowsHide: true,
  });

  child.unref();

  // Wait for "DAEMON_READY:<port>" on stdout
  return new Promise<number>((resolve, reject) => {
    let output = '';
    let exitCode: number | null = null;
    let exitSignal: NodeJS.Signals | null = null;
    let settled = false;
    const cleanup = () => {
      clearTimeout(timeout);
      child.stdout?.destroy();
    };
    const resolveOnce = (port: number) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(port);
    };
    const rejectOnce = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const startedAt = Date.now();
    const timeout = setTimeout(() => {
      const error = new Error('Daemon startup timed out');
      rejectOnce(
        attachCliDiagnosticMetadata(
          error,
          daemonSpawnTelemetry({
            phase: 'spawn-timeout',
            cliEntry,
            candidates,
            childPid: child.pid,
            startedAt,
            output,
            exitCode,
            exitSignal,
          })
        )
      );
    }, HEALTH_POLL_TIMEOUT_MS);

    child.stdout!.on('data', (data: Buffer) => {
      output += data.toString();
      const match = output.match(/DAEMON_READY:(\d+)/);
      if (match) {
        resolveOnce(parseInt(match[1], 10));
      }
    });

    child.on('error', (err) => {
      rejectOnce(
        attachCliDiagnosticMetadata(
          err,
          daemonSpawnTelemetry({
            phase: 'spawn-error',
            cliEntry,
            candidates,
            childPid: child.pid,
            startedAt,
            output,
            exitCode,
            exitSignal,
          })
        )
      );
    });

    child.on('exit', (code, signal) => {
      exitCode = code;
      exitSignal = signal;
      if (!output.includes('DAEMON_READY')) {
        const exitDescription = code !== null ? `code ${code}` : `signal ${signal ?? 'unknown'}`;
        const error = new Error(`Daemon exited with ${exitDescription} before becoming ready`);
        rejectOnce(
          attachCliDiagnosticMetadata(
            error,
            daemonSpawnTelemetry({
              phase: 'spawn-exit',
              cliEntry,
              candidates,
              childPid: child.pid,
              startedAt,
              output,
              exitCode: code,
              exitSignal: signal,
            })
          )
        );
      }
    });
  });
}

/* ── Public API ──────────────────────────────────────────────────────── */

/**
 * Return the port if the pidfile points at a live, healthy daemon. Otherwise
 * clean up the stale pidfile and return null.
 */
async function readHealthyDaemonPort(): Promise<number | null> {
  const state = await readPidfile();
  if (!state) return null;
  if (isProcessAlive(state.pid) && (await healthCheck(state.port))) {
    return state.port;
  }
  // Stale pidfile — clean up so a fresh daemon can claim it.
  try {
    await fs.unlink(PIDFILE_PATH);
  } catch {
    // ignore
  }
  return null;
}

/** True when the spawn lock is held by a dead owner or is older than the TTL. */
async function isSpawnLockStale(): Promise<boolean> {
  try {
    const { pid, at } = JSON.parse(await fs.readFile(LOCKFILE_PATH, 'utf-8')) as {
      pid?: number;
      at?: string;
    };
    if (typeof pid === 'number' && !isProcessAlive(pid)) return true;
    if (at && Date.now() - new Date(at).getTime() > SPAWN_LOCK_STALE_MS) return true;
    return false;
  } catch {
    // Missing or corrupt — safe to treat as stale.
    return true;
  }
}

/**
 * Run `fn` while holding an exclusive on-disk lock, so concurrent CLI/MCP
 * invocations (or different CLI builds) can't each spawn a competing daemon.
 * The lock is best-effort: if it can't be acquired within the timeout we run
 * anyway — correctness is still guaranteed by the re-check inside `fn` and the
 * daemon-side singleton guard.
 */
async function withSpawnLock<T>(fn: () => Promise<T>): Promise<T> {
  await fs.mkdir(PIDFILE_DIR, { recursive: true, mode: 0o700 });
  const deadline = Date.now() + SPAWN_LOCK_TIMEOUT_MS;
  let held = false;
  while (Date.now() < deadline) {
    try {
      const fh = await fs.open(LOCKFILE_PATH, 'wx', 0o600);
      await fh.writeFile(JSON.stringify({ pid: process.pid, at: new Date().toISOString() }));
      await fh.close();
      held = true;
      break;
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code !== 'EEXIST') throw err;
      // Lock held by someone else — steal it if stale, else wait and retry.
      if (await isSpawnLockStale()) {
        try {
          await fs.unlink(LOCKFILE_PATH);
        } catch {
          // ignore
        }
        continue;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }
  try {
    return await fn();
  } finally {
    if (held) {
      try {
        await fs.unlink(LOCKFILE_PATH);
      } catch {
        // ignore
      }
    }
  }
}

/**
 * Ensure exactly one daemon is running and return its port. Starts the daemon
 * if needed and cleans up stale pidfiles. Spawning is serialized by an on-disk
 * lock so racing invocations converge on a single daemon instead of each
 * starting one and clobbering the shared pidfile.
 */
async function ensureDaemon(): Promise<number> {
  // Fast path: a healthy daemon already owns the pidfile.
  const existing = await readHealthyDaemonPort();
  if (existing !== null) return existing;

  // Slow path: serialize spawning so we never start two daemons at once.
  return withSpawnLock(async () => {
    // Re-check under the lock — another invocation may have spawned one while
    // we were waiting to acquire it.
    const winner = await readHealthyDaemonPort();
    if (winner !== null) return winner;

    const port = await spawnDaemon();

    // Poll until healthy.
    const deadline = Date.now() + HEALTH_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      if (await healthCheck(port)) return port;
      await new Promise((r) => setTimeout(r, HEALTH_POLL_INTERVAL_MS));
    }

    throw new Error('Daemon failed to become healthy after spawn');
  });
}

/* ── Session operations ──────────────────────────────────────────────── */

export async function createSession(
  params: CreateSessionRequest
): Promise<DaemonResponse<SessionInfo>> {
  return daemonFetchWithRetry('POST', '/sessions', params) as Promise<DaemonResponse<SessionInfo>>;
}

export async function listSessions(): Promise<DaemonResponse<SessionInfo[]>> {
  return daemonFetchWithRetry('GET', '/sessions') as Promise<DaemonResponse<SessionInfo[]>>;
}

export async function getSession(sessionId: string): Promise<DaemonResponse<SessionInfo>> {
  return daemonFetchWithRetry('GET', `/sessions/${sessionId}`) as Promise<
    DaemonResponse<SessionInfo>
  >;
}

export async function deleteSession(
  sessionId: string,
  telemetry: Pick<DaemonRequestTelemetry, 'sessionTargetSource'> = {}
): Promise<DaemonResponse> {
  return daemonFetchWithRetry('DELETE', `/sessions/${sessionId}`, undefined, {
    operation: 'session.stop',
    sessionId,
    sessionTargetSource: telemetry.sessionTargetSource,
  }) as Promise<DaemonResponse>;
}

export async function deleteAllSessions(): Promise<DaemonResponse> {
  return daemonFetchWithRetry('DELETE', '/sessions', undefined, {
    operation: 'session.stop-all',
    sessionTargetSource: 'all',
  }) as Promise<DaemonResponse>;
}

export async function swapSessionContext(
  sessionId: string,
  params: SwapContextRequest
): Promise<DaemonResponse<SessionInfo>> {
  return daemonFetchWithRetry('POST', `/sessions/${sessionId}/swap-context`, params) as Promise<
    DaemonResponse<SessionInfo>
  >;
}

export async function getSessionStorageState(sessionId: string): Promise<StorageStateResponse> {
  return daemonFetchWithRetry(
    'GET',
    `/sessions/${sessionId}/storage-state`
  ) as Promise<StorageStateResponse>;
}

export async function callTool(
  sessionId: string,
  toolName: string,
  args: Record<string, unknown>
): Promise<ToolResponse> {
  return daemonFetchWithRetry(
    'POST',
    `/sessions/${sessionId}/tools/${toolName}`,
    args
  ) as Promise<ToolResponse>;
}

/* ── Lock management ────────────────────────────────────────────────── */

export async function takeControl(sessionId: string): Promise<DaemonResponse<SessionInfo>> {
  return daemonFetchWithRetry('POST', `/sessions/${sessionId}/take-control`) as Promise<
    DaemonResponse<SessionInfo>
  >;
}

export async function resumeCloud(sessionId: string): Promise<DaemonResponse<SessionInfo>> {
  return daemonFetchWithRetry('POST', `/sessions/${sessionId}/resume-cloud`) as Promise<
    DaemonResponse<SessionInfo>
  >;
}

/**
 * Resolve the target session for a command.
 * If there's exactly one session, auto-targets it.
 * If a sessionId or name is provided, looks it up.
 */
export async function resolveTargetSession(sessionIdOrName?: string): Promise<SessionInfo> {
  const result = await listSessions();
  if (!result.ok || !result.data) {
    throw new Error('Failed to list sessions');
  }
  const sessions = result.data;

  if (sessions.length === 0) {
    throw new Error('No active sessions. Start one with: canary session start');
  }

  if (sessionIdOrName) {
    const match = sessions.find((s) => s.id === sessionIdOrName || s.name === sessionIdOrName);
    if (!match) {
      throw new Error(
        `Session "${sessionIdOrName}" not found. Active sessions: ${sessions.map((s) => s.id).join(', ')}`
      );
    }
    return match;
  }

  if (sessions.length === 1) {
    return sessions[0];
  }

  throw new Error(
    `Multiple sessions active. Specify one with --session:\n${sessions.map((s) => `  ${s.id} (${s.name})`).join('\n')}`
  );
}
